import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';

import { ReactComponent as WarningIcon } from '../../img/warning-filled.svg';
import { Asterisk } from '../datePicker/Calendar.styled';
import Icon from '../icon/Icon';
import { getBaseInputStyles, getInputBorderStyles, getInputCaptionColor } from '../input/Input';
import Caption from '../typography/Caption';
import { useAutoTextAreaSize } from './useAutoTextAreaSize';
export interface TextAreaProps {
    assistiveText?: string;
    disabled?: boolean;
    grow?: boolean;
    maxLength?: number;
    notice?: string;
    onChange: (text: string) => void;
    placeholder?: string;
    required?: boolean;
    shouldAutoFocus?(): boolean;
    title?: string;
    value: string;
    valid?: boolean;
    rows?: number;
}

const DEFAULT_ROWS_WITH_GROW = 0;
const DEFAULT_ROWS = 5;

const TextArea = (props: TextAreaProps): JSX.Element => {
    const {
        assistiveText,
        disabled,
        grow = false,
        valid = true,
        maxLength,
        notice,
        onChange,
        placeholder,
        required,
        rows: rowsProp,
        shouldAutoFocus,
        title,
        value
    } = props;
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const [ isActive, setIsActive ] = useState(false);

    const theme = useContext(ThemeContext)!;

    const rows = useMemo(() => {
        if (rowsProp === undefined) {
            return grow ? DEFAULT_ROWS_WITH_GROW : DEFAULT_ROWS;
        }

        return rowsProp;
    }, [ rowsProp, grow ]);

    useEffect(function handleAutofocus() {
        if (shouldAutoFocus?.() && textAreaRef.current) {
            const textArea = textAreaRef.current;

            textArea.focus();
            textArea.setSelectionRange(value.length, value.length);
        }
    }, [ shouldAutoFocus, value ]);

    const handleChangeText = (event: React.FormEvent<HTMLTextAreaElement>) => {
        const eventText = event.currentTarget.value;

        onChange(eventText);
    };

    const renderAssistiveIcon = () => {
        if (!notice || value.length === 0) {
            return null;
        }

        return <Icon icon={ <WarningIcon /> }
            color={ theme.semanticColor.support_03 }
            size={ 20 }
            tooltipText={ notice }
        />;
    };

    useAutoTextAreaSize(textAreaRef, grow, value);

    return (
        <Root>
            <Wrapper active={ isActive }
                disabled={ disabled }
                valid={ valid }
            >
                { title && <TitleWrapper>
                    <Title active={ isActive }
                        disabled={ !!disabled }
                    >
                        { title }{ required ? <Asterisk>*</Asterisk> : null }
                    </Title>

                    { renderAssistiveIcon() }
                </TitleWrapper> }

                <TextInput active={ isActive }
                    onBlur={ () => setIsActive(false) }
                    onChange={ handleChangeText }
                    onFocus={ () => setIsActive(true) }
                    maxLength={ maxLength }
                    padRight={ !!notice }
                    placeholder={ placeholder }
                    ref={ textAreaRef }
                    rows={ rows }
                    title={ title }
                    value={ value }
                />
            </Wrapper>

            <AssistiveText>
                { assistiveText && assistiveText }
            </AssistiveText>
        </Root>
    );
};

const Root = styled.div`
    display: flex;
    flex-direction: column;
`;

const Wrapper = styled.div<{ active: boolean; disabled?: boolean; valid?: boolean }>`
    display: flex;
    background-color: transparent;
    border-radius: ${ ({ theme }) => theme.radius.radius_5 };
    flex-direction: column;
    position: relative;
  
    ${ getInputBorderStyles }
`;

const AssistiveText = styled(Caption)`
    align-self: flex-end;
    display: flex;
    position: relative;
    white-space: nowrap;

    ${ ({ theme }) => `
        margin-top: ${ theme.size.size_5 };
        margin-right: ${ theme.size.size_20 };
        color: ${ theme.semanticColor.textSecondary };
    ` };
`;

const TitleWrapper = styled.div`
    background-color: white;
    height: ${ ({ theme }) => theme.size.size_30 };
    justify-content: flex-end;
    left: ${ ({ theme }) => theme.size.size_20 };
    position: absolute;
    right: ${ ({ theme }) => theme.size.size_20 };
    top: 0;
    
    ${ Icon } {
        position: absolute;
        right: 0;
        top: ${ ({ theme }) => theme.size.size_10 };
    }
`;

const Title = styled(Caption)<{ active: boolean; disabled: boolean }>`
    ${ getInputCaptionColor };
    left: 0;
    position: absolute;
    top: ${ ({ theme }) => theme.size.size_10 };
`;

const TextInput  = styled.textarea<{ active: boolean; padRight: boolean; title?: string }>`
    border: 0;
    padding-bottom: ${ ({ theme }) => theme.size.size_10 };
    padding-left: ${ ({ theme }) => theme.size.size_20 };
    padding-right: ${ ({ padRight, theme }) => (padRight ? theme.size.size_60 : theme.size.size_20) };
    padding-top: ${ ({ title, theme }) => (title ? theme.size.size_30 : theme.size.size_10)  };
    resize: none;
    width: 100%;

    ${ getBaseInputStyles };

    font-size: 16px;
    line-height: ${ ({ theme }) => theme.size.size_30 };

    ::placeholder {
        color: ${ ({ theme }) => theme.color.grey.grey_40 };
        font-size: ${ ({ theme }) => theme.size.size_20 };
        line-height: ${ ({ theme }) => theme.size.size_30 };
    };
`;
  
export default TextArea;
